import Vue from "vue";

/**
 * InfinitScrollController 用于统一管理无限滚动列表的数据加载、参数筛选、追加加载（loadMore）等逻辑。
 * 用法示例：
 *
 * import { InfinitScrollController } from '@/controllers/InfinitScrollController'
 *
 * export default {
 *   data() {
 *     return {
 *       listCtrl: new InfinitScrollController({
 *         loadMethod: this.fetchData, // 必须返回 { list: [], totalCount: 100 }
 *         pageSize: 20,
 *         keyField: 'id',
 *         queryParameters: { status: '', keyword: '' }
 *       })
 *     }
 *   },
 *   methods: {
 *     async fetchData(params) {
 *       // 返回 Promise，格式 { list: [], totalCount: 100 }
 *       const res = await apiFetchTable(params)
 *       return { list: res.data, totalCount: res.total }
 *     }
 *   }
 * }
 */

export class InfiniteScrollController {
  constructor(options) {
    this.loadMethod = options.loadMethod;
    this.pageSize = options.pageSize || 15;
    this.keyField = options.keyField || "id";
    this.pageBase = typeof options.pageBase === "number" ? options.pageBase : 1;
    this.pageField = options.pageField || "page";
    this.pageSizeField = options.pageSizeField || "pageSize";
    this.queryParameters = Vue.observable(options.queryParameters || {});
    this.state = Vue.observable({
      list: [],
      loading: false,
      noMore: false,
      pagination: {
        page: this.pageBase,
        pageSize: this.pageSize,
        total: 0,
      },
    });
  }

  async loadMore() {
    if (this.state.loading || this.state.noMore) return;
    this.state.loading = true;
    try {
      const params = {
        [this.pageField]: this.state.pagination.page,
        [this.pageSizeField]: this.state.pagination.pageSize,
        ...this.queryParameters,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || Number.isNaN(params[key])) {
          delete params[key];
        }
      });
      const response = await this.loadMethod(params);
      const newList = response.list || [];
      if (newList.length === 0) {
        this.state.noMore = true;
      } else {
        this.state.list = this.state.list.concat(newList);
        this.state.pagination.page += 1;
        this.state.pagination.total = response.totalCount || this.state.list.length;
        if (newList.length < this.state.pagination.pageSize) {
          this.state.noMore = true;
        }
      }
    } catch (e) {
      this.state.noMore = true;
    }
    this.state.loading = false;
  }

  async reload(params = {}) {
    this.state.list = [];
    this.state.pagination.page = this.pageBase;
    this.state.noMore = false;
    Object.assign(this.queryParameters, params);
    await this.loadMore();
  }

  setQueryParameters(params) {
    Object.assign(this.queryParameters, params);
  }

  reset() {
    this.state.list = [];
    this.state.pagination.page = this.pageBase;
    this.state.noMore = false;
    this.state.pagination.total = 0;
  }
}
